import React, { useContext, useState } from 'react'
import {
  Button,
  Typography,
  Box,
  Divider,
  SelectChangeEvent,
  Stepper,
  Step,
  StepLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material'
import {
  validatePhoneNumber,
  validateZipCode,
  validateTaxId,
  formatTaxId,
  validateEmail,
  formatPhoneNumber,
  formatZipCode,
  formatAveragePurchase,
  validateAveragePurchase
} from 'utils/formatUtils'
import FixedWidthContainer from 'components/FixedWidthContainer'
import SectionHeader from 'components/SectionHeader'
import { SnackbarContext } from 'contexts/SnackbarContext'
import { WIX_HOME_URL } from 'config'
import SignUpStep1Form from 'pages/SignUp/SignUpStep1Form'
import { useNavigate } from 'react-router-dom'
import SignUpStep2Form from 'pages/SignUp/SignUpStep2Form'
import { signUp } from 'api/signUp'
import { FormValues, validateSignUpStep } from 'utils/validateSignUpSteps'
import { LoadingContext } from 'contexts/LoadingContext'
import { useTranslation } from 'react-i18next'

const initialValues: FormValues = {
  email: '',
  firstName: '',
  lastName: '',
  phoneNumber: '',
  faxNumber: '',
  companyName: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  zipCode: '',
  source: '',
  businessType: '',
  currentSupplier: '',
  averagePurchase: '',
  services: [],
  taxId: ''
}

type FormErrors = {
  [key: string]: boolean
}

const SignUpPage = () => {
  const [formValues, setFormValues] = useState<FormValues>(initialValues)
  const { showMessage } = useContext(SnackbarContext)
  const navigate = useNavigate()
  const { showLoading, stopLoading } = useContext(LoadingContext)
  const [showSuccessDialog, setShowSuccessDialog] = useState(false)
  const { t } = useTranslation()

  const [errors, setErrors] = useState<FormErrors>({
    email: false,
    firstName: false,
    lastName: false,
    phoneNumber: false,
    faxNumber: false,
    companyName: false,
    addressLine1: false,
    city: false,
    state: false,
    zipCode: false,
    source: false,
    businessType: false,
    currentSupplier: false,
    averagePurchase: false,
    services: false,
    taxId: false
  })

  const [currentStep, setCurrentStep] = useState<number>(1)

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target
    let formattedValue = value

    if (name === 'email') {
      formattedValue = value.replace(/\s/g, '')
    }

    if (name === 'taxId') {
      formattedValue = formatTaxId(value)
    }

    if (name === 'phoneNumber') {
      formattedValue = formatPhoneNumber(value)
    }

    if (name === 'faxNumber') {
      formattedValue = formatPhoneNumber(value)
    }

    if (name === 'zipCode') {
      formattedValue = formatZipCode(value)
    }

    if (name === 'averagePurchase') {
      formattedValue = formatAveragePurchase(value)
    }
    setFormValues({
      ...formValues,
      [name]: formattedValue
    })
  }

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target
    let isValid = true

    if (
      [
        'firstName',
        'lastName',
        'companyName',
        'addressLine1',
        'city',
        'state',
        'currentSupplier',
        'averagePurchase',
        'taxId'
      ].includes(name)
    ) {
      isValid = value.trim() !== ''
      if (isValid && name === 'taxId') {
        isValid = validateTaxId(value)
      }
      if (isValid && name === 'averagePurchase') {
        isValid = validateAveragePurchase(value)
      }
    } else if (name === 'phoneNumber') {
      isValid = validatePhoneNumber(value)
    } else if (name === 'faxNumber') {
      isValid = value.trim() === '' || validatePhoneNumber(value)
    } else if (name === 'zipCode') {
      isValid = validateZipCode(value)
    } else if (name === 'source' || name === 'businessType') {
      isValid = value.trim() !== ''
    } else if (name === 'email') {
      isValid = validateEmail(value)
    }

    setErrors({
      ...errors,
      [name]: !isValid
    })
  }

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target
    if (name) {
      setFormValues({ ...formValues, [name]: value })
      setErrors({ ...errors, [name]: value.trim() === '' })
    }
  }

  const handleServicesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target
    let updatedServices: string[] = [...formValues.services]
    if (checked) {
      updatedServices.push(name)
    } else {
      updatedServices = updatedServices.filter(service => service !== name)
    }
    setFormValues({
      ...formValues,
      services: updatedServices
    })
    setErrors({
      ...errors,
      services: updatedServices.length === 0
    })
  }

  const handleClear = () => {
    setFormValues(initialValues)
    setErrors({
      email: false,
      firstName: false,
      lastName: false,
      phoneNumber: false,
      faxNumber: false,
      companyName: false,
      addressLine1: false,
      city: false,
      state: false,
      zipCode: false,
      source: false,
      businessType: false,
      currentSupplier: false,
      averagePurchase: false,
      services: false,
      taxId: false
    })
    setCurrentStep(1)
  }

  const validateStep = (step: number): FormErrors =>
    validateSignUpStep(step, formValues)

  const handleNext = () => {
    const stepErrors = validateStep(currentStep)
    setErrors({ ...errors, ...stepErrors })

    const hasErrors = Object.values(stepErrors).some(value => value)

    if (!hasErrors) {
      setCurrentStep(prev => prev + 1)
    }
  }

  const handleBack = () => {
    setCurrentStep(prev => prev - 1)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const step1Errors = validateStep(1)
    const step2Errors = validateStep(2)
    const newErrors = { ...step1Errors, ...step2Errors }
    setErrors(newErrors)

    const hasErrors = Object.values(newErrors).some(value => value)

    if (!hasErrors) {
      showLoading()
      const result = await signUp(formValues)
      if (result.success) {
        setShowSuccessDialog(true)
        setFormValues(initialValues)
      } else {
        if (result.exists) {
          showMessage(result.error, 'error')
          navigate('/login')
        } else {
          showMessage(result.error, 'error')
        }
      }
    }
    stopLoading()
  }

  const handleDialogClose = () => {
    setShowSuccessDialog(false)
    window.location.assign(WIX_HOME_URL)
  }

  return (
    <FixedWidthContainer sx={{ my: '50px' }}>
      <SectionHeader title={t('signup.title')} />

      <Box display='flex' justifyContent='space-between' alignItems='center'>
        <Typography variant='h2'>
          {currentStep === 1 && t('signup.step1Title')}
          {currentStep === 2 && t('signup.step2Title')}
        </Typography>

        <Button
          variant='outlined'
          sx={{
            borderRadius: '6px',
            px: 2,
            py: 1,
            textTransform: 'none',
            fontWeight: 400
          }}
          onClick={handleClear}
        >
          {t('signup.clearButton')}
        </Button>
      </Box>
      <Divider sx={{ my: 2 }} />

      <Stepper activeStep={currentStep - 1} alternativeLabel>
        <Step key='Step1'>
          <StepLabel>{t('signup.step1Label')}</StepLabel>
        </Step>
        <Step key='Step2'>
          <StepLabel>{t('signup.step2Label')}</StepLabel>
        </Step>
      </Stepper>

      <Box component='form' noValidate onSubmit={handleSubmit} sx={{ mt: 3 }}>
        {currentStep === 1 && (
          <SignUpStep1Form
            formValues={formValues}
            errors={errors}
            handleInputChange={handleInputChange}
            handleBlur={handleBlur}
            handleSelectChange={handleSelectChange}
            handleNext={handleNext}
            t={t}
          />
        )}

        {currentStep === 2 && (
          <SignUpStep2Form
            formValues={formValues}
            errors={errors}
            handleInputChange={handleInputChange}
            handleBlur={handleBlur}
            handleServicesChange={handleServicesChange}
            handleSelectChange={handleSelectChange}
            handleBack={handleBack}
            handleSubmit={handleSubmit}
            t={t}
          />
        )}
      </Box>

      <Dialog open={showSuccessDialog} onClose={handleDialogClose}>
        <DialogTitle>{t('signup.successTitle')}</DialogTitle>
        <DialogContent>
          <Typography>{t('signup.successMessage')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color='primary'>
            {t('signup.successButton')}
          </Button>
        </DialogActions>
      </Dialog>
    </FixedWidthContainer>
  )
}

export default SignUpPage
